import { useTranslation } from "react-i18next";

import { ChannelCell } from "./ChannelCell";
import { CountryPricesCell } from "./CountryPricesCell";
import { EntryStatusBadge } from "./EntryStatusBadge";
import { HistoryToggleButton } from "./HistoryToggleButton";
import type { IFinanceGridChannel, IFinanceGridPriceEntry } from "./types";
import { entryStatus, formatGridPrice, formatPeriodLabel } from "./utils";

/** Une ligne de prix. La cellule canal et le bouton d'historique ne s'affichent que sur la première ligne du canal. */
export const FinanceGridRow = ({
    channel,
    entry,
    isFirstOfChannel,
    pastCount,
    isHistoryOpen,
    onToggleHistory,
}: {
    channel: IFinanceGridChannel;
    entry: IFinanceGridPriceEntry;
    isFirstOfChannel: boolean;
    pastCount: number;
    isHistoryOpen: boolean;
    onToggleHistory: () => void;
}) => {
    const [t] = useTranslation();
    const status = entryStatus(entry);
    const isPast = status === "past";
    const periodLabel = formatPeriodLabel(entry, t("PriceHistory.OpenEnded"));

    return (
        <tr
            className={`border-b border-border-color last:border-b-0 hover:bg-backgroundZ1 ${
                isPast ? "opacity-50" : ""
            }`}
        >
            <td className="py-1.5 px-3 align-middle">{isFirstOfChannel && <ChannelCell channel={channel} />}</td>
            <td className="py-1.5 px-3 align-middle text-right">
                <span className="text-sm tabular-nums text-grey-900">
                    {formatGridPrice(entry.price, entry.currency)}
                </span>
            </td>
            <td className="py-1.5 px-3 align-middle">
                <span className="text-xs font-mono text-foreground">{entry.currency}</span>
            </td>
            <td className="py-1.5 px-3 align-middle">
                <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                    {periodLabel && <span className="text-xs tabular-nums text-foreground">{periodLabel}</span>}
                    <EntryStatusBadge status={status} />
                </div>
            </td>
            <td className="py-1.5 px-3 align-middle">
                <CountryPricesCell childCurrencies={entry.childCurrencies} />
            </td>
            <td className="py-1.5 px-3 align-middle text-right">
                {isFirstOfChannel && pastCount > 0 && (
                    <HistoryToggleButton isOpen={isHistoryOpen} pastCount={pastCount} onToggle={onToggleHistory} />
                )}
            </td>
        </tr>
    );
};
